import React, { useMemo } from "react";
import { PlayerRow } from "./PlayerRow";
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';
import { MultiSelectDropdown } from '../../../components/common/MultiSelectDropdown';
import { HeaderSearchBox } from '../../../components/common/HeaderSearchBox';
import { HeaderTeamDropdown } from '../../../components/common/HeaderTeamDropdown';

const DEFAULT_HITTING_COLUMNS = [
  { label: 'AB', key: 'ab', statId: 'ab' },
  { label: 'AVG', key: 'avg', statId: 'avg' },
  { label: 'OBP', key: 'ob_pct', statId: 'obp' },
  { label: 'SLG', key: 'slg_pct', statId: 'slg' },
  { label: 'R', key: 'r', statId: 'runs' },
  { label: 'H', key: 'h', statId: 'hits' },
  { label: '2B', key: '2b', statId: '2b' },
  { label: '3B', key: '3b', statId: '3b' },
  { label: 'HR', key: 'hr', statId: 'hr' },
  { label: 'K', key: 'so', statId: 'k' },
  { label: 'BB', key: 'bb', statId: 'bb' },
];

const DEFAULT_PITCHING_COLUMNS = [
  { label: 'IP', key: 'innings_pitched', statId: 'ip' },
  { label: 'GS', key: 'games_started', statId: 'gs' },
  { label: 'ERA', key: 'era', statId: 'era' },
  { label: 'WHIP', key: 'WHIP', statId: 'whip' },
  { label: 'SO', key: 'strikeouts_pitched', statId: 'so' },
  { label: 'BB', key: 'bb', statId: 'bb' },
  { label: 'K%', key: 'k_pct', calculated: true, statId: 'k%' },
  { label: 'BB%', key: 'bb_pct', calculated: true, statId: 'bb%' },
];

export const PlayerTable = ({
  data = [],
  loading = false,
  error = null,
  pagination = null,
  onPageChange = null,
  limit = 20,
  onLimitChange = null,
  followedPlayers = [],
  showNewSchool = false,
  selectedStatColumns = [],
  filterActiveTab = null,
  statCategory = 'HITTING',
  sortColumn = null,
  sortDirection = 'asc',
  onSort = null,
  selectedPositions = [],
  onPositionFilter = null,
  nameSearch = '',
  onNameSearch = null,
  selectedTeam = null,
  onTeamSelect = null,
  onResetFilters = null
}) => {
  // Only show loading spinner if there's no data yet (initial load)
  const isInitialLoading = loading && data.length === 0;

  // Mapping from UI column key to backend sort field name
  const columnToBackendSort = {
    'jpRank': 'finalScore',
    'year': 'playerClass',
    'name': 'firstName',
    'position': 'position',
    'team': 'teamName',
    'class': 'playerClass',
    'region': 'region',
    'playerScore': 'finalScore',
  };

  // Mapping from stat column ID to backend sort field name
  const statToBackendSort = {
    'avg': 'batting_average',
    'obp': 'on_base_percentage',
    'slg': 'slugging_percentage',
    'ops': 'on_base_plus_slugging',
    'runs': 'runs',
    'hits': 'hits',
    '2b': 'doubles',
    '3b': 'triples',
    'hr': 'home_runs',
    'rbi': 'rbi',
    'tb': 'total_bases',
    'bb': 'walks',
    'k': 'strikeouts',
    'k%': 'strikeout_percentage',
    'bb%': 'walk_percentage',
    'k/bb': 'K_BB_ratio',
    'ops+': 'ops_plus',
    'sb': 'stolen_bases',
    'cs': 'caught_stealing',
    'ab': 'at_bats',
    'hbp': 'hit_by_pitch',
    'sac': 'sacrifice_hits',
    'sf': 'sacrifice_flies',
    'gp': 'games_played',
    'gs': 'games_started',
    // Pitching
    'era': 'era',
    'app': 'appearances',
    'w': 'wins',
    'l': 'losses',
    'so': 'strikeouts_pitched',
    'ip': 'innings_pitched',
    'whip': 'WHIP',
    'cg': 'complete_games',
    'sho': 'shutouts',
    'sv': 'saves',
    'h': 'hits_allowed',
    'r': 'runs_allowed',
    'er': 'earned_runs',
    'baa': 'batting_average_against',
    'wp': 'wild_pitches',
    'bk': 'balks',
    // Fielding
    'fld%': 'fielding_percentage',
    'e': 'errors',
    'po': 'putouts',
    'a': 'assists',
    'dp': 'double_plays',
    'tc': 'total_chances',
    'sba': 'f_stolen_bases_against',
    'rcs': 'runners_caught_stealing',
    'rcs%': 'runners_caught_stealing_percentage',
    'pb': 'passed_balls',
    // Advanced stats — backend field names (exact case)
    'iso': 'iso',
    'woba': 'wOBA',
    'wrc+': 'wRC_plus',
    'pa': 'plate_appearances',
    'babip': 'babip',
    'fip': 'FIP',
    'k-bb%': 'K_BB_percent',
    'fip+': 'FIP_plus',
    'era+': 'ERA_plus',
    'xfip': 'xFIP',
    'lob%': 'LOB_percent',
    'babip-against': 'BABIP_against',
  };

  // Define stat column mappings (same as StatsTable)
  const statColumnMapping = {
    // Hitting stats
    'avg': { label: 'AVG', key: 'avg', statId: 'avg' },
    'obp': { label: 'OBP', key: 'ob_pct', statId: 'obp' },
    'slg': { label: 'SLG', key: 'slg_pct', statId: 'slg' },
    'ops': { label: 'OPS', key: 'ops', calculated: true, statId: 'ops' },
    'ops+': { label: 'OPS+', key: 'ops_plus', statId: 'ops+' },
    'runs': { label: 'R', key: 'r', statId: 'runs' },
    'hits': { label: 'H', key: 'h', statId: 'hits' },
    '2b': { label: '2B', key: '2b', statId: '2b' },
    '3b': { label: '3B', key: '3b', statId: '3b' },
    'hr': { label: 'HR', key: 'hr', statId: 'hr' },
    'rbi': { label: 'RBI', key: 'rbi', statId: 'rbi' },
    'tb': { label: 'TB', key: 'tb', statId: 'tb' },
    'bb': { label: 'BB', key: 'bb', statId: 'bb' },
    'k': { label: 'K', key: 'so', statId: 'k' },
    'k%': { label: 'K%', key: 'k_pct', calculated: true, statId: 'k%' },
    'bb%': { label: 'BB%', key: 'bb_pct', calculated: true, statId: 'bb%' },
    'k/bb': { label: 'K/BB', key: 'k_bb_ratio', statId: 'k/bb' },
    'sb': { label: 'SB', key: 'sb', statId: 'sb' },
    'cs': { label: 'CS', key: 'cs', statId: 'cs' },
    // Fielding stats
    'fld%': { label: 'FLD%', key: 'fielding_percentage', isFielding: true, statId: 'fld%' },
    'e': { label: 'E', key: 'errors', isFielding: true, statId: 'e' },
    'po': { label: 'PO', key: 'putouts', isFielding: true, statId: 'po' },
    'a': { label: 'A', key: 'assists', isFielding: true, statId: 'a' },
    'dp': { label: 'DP', key: 'double_plays', isFielding: true, statId: 'dp' },
    'tc': { label: 'TC', key: 'total_chances', isFielding: true, statId: 'tc' },
    'sba': { label: 'SBA', key: 'stolen_bases_against', isFielding: true, statId: 'sba' },
    'rcs': { label: 'RCS', key: 'runners_caught_stealing', isFielding: true, statId: 'rcs' },
    'rcs%': { label: 'RCS%', key: 'runners_caught_stealing_percentage', isFielding: true, statId: 'rcs%' },
    'pb': { label: 'PB', key: 'passed_balls', isFielding: true, statId: 'pb' },
    // Pitching stats
    'w': { label: 'W', key: 'wins', statId: 'w' },
    'l': { label: 'L', key: 'losses', statId: 'l' },
    'era': { label: 'ERA', key: 'era', statId: 'era' },
    'whip': { label: 'WHIP', key: 'WHIP', statId: 'whip' },
    'ip': { label: 'IP', key: 'innings_pitched', statId: 'ip' },
    'so': { label: 'SO', key: 'strikeouts_pitched', statId: 'so' },
    'gs': { label: 'GS', key: 'games_started', statId: 'gs' },
    'app': { label: 'APP', key: 'games_pitched', statId: 'app' },
    'cg': { label: 'CG', key: 'complete_games', statId: 'cg' },
    'sho': { label: 'SHO', key: 'shutouts', statId: 'sho' },
    'sv': { label: 'SV', key: 'saves', statId: 'sv' },
    'h': { label: 'H', key: 'hits_allowed', statId: 'h' },
    'r': { label: 'R', key: 'runs_allowed', statId: 'r' },
    'er': { label: 'ER', key: 'earned_runs', statId: 'er' },
    'baa': { label: 'BAA', key: 'batting_average_against', statId: 'baa' },
    'ab': { label: 'AB', key: 'at_bats', statId: 'ab' },
    'wp': { label: 'WP', key: 'wild_pitches', statId: 'wp' },
    'hbp': { label: 'HBP', key: 'hit_batters', statId: 'hbp' },
    'bk': { label: 'BK', key: 'balks', statId: 'bk' },
    'iso': { label: 'ISO', key: 'iso', statId: 'iso' },
    'woba': { label: 'wOBA', key: 'woba', statId: 'woba' },
    'wrc+': { label: 'wRC+', key: 'wrc_plus', statId: 'wrc+' },
    'pa': { label: 'PA', key: 'pa', statId: 'pa' },
    'babip': { label: 'BABIP', key: 'babip', statId: 'babip' },
    'fip': { label: 'FIP', key: 'fip', statId: 'fip' },
    'k-bb%': { label: 'K-BB%', key: 'k_bb_pct', statId: 'k-bb%' },
    'fip+': { label: 'FIP+', key: 'fip_plus', statId: 'fip+' },
    'era+': { label: 'ERA+', key: 'era_plus', statId: 'era+' },
    'xfip': { label: 'xFIP', key: 'xfip', statId: 'xfip' },
    'lob%': { label: 'LOB%', key: 'lob_pct', statId: 'lob%' },
    'babip-against': { label: 'BABIP Against', key: 'babip_against', statId: 'babip-against' },
  };

  // Determine dynamic stat columns based on filters or default category
  const dynamicStatColumns = useMemo(() => {
    if (selectedStatColumns.length > 0) {
      return selectedStatColumns.map(col => statColumnMapping[col]).filter(Boolean);
    }
    if (statCategory === 'PITCHING') return DEFAULT_PITCHING_COLUMNS;
    if (statCategory === 'HITTING') return DEFAULT_HITTING_COLUMNS;
    return [];
  }, [selectedStatColumns, statCategory]);

  const formatStat = (value, columnKey) => {
    if (value === null || value === undefined || value === '') return '0';
    if (typeof value === 'number') {
      if (columnKey === 'slg_pct' || columnKey === 'slugging_percentage' || columnKey === 'ob_pct' || columnKey === 'on_base_percentage' || columnKey === 'avg' || columnKey === 'batting_average' || columnKey === 'batting_average_against' || columnKey === 'baa') return value.toFixed(3);
      if (value === 0) return '0';
      if (columnKey === 'era' || columnKey === 'ERA' || columnKey === 'xfip' || columnKey === 'xFIP' || columnKey === 'WHIP' || columnKey === 'whip') return value.toFixed(2);
      if (columnKey === 'ops' || columnKey === 'on_base_plus_slugging') return value.toFixed(3);
      if (columnKey === 'ops_plus' || columnKey === 'wrc_plus') return Math.round(value).toString();
      if (columnKey === 'innings_pitched' || columnKey === 'ip' || columnKey === 'inningsPitched') return value.toFixed(1);
      return value % 1 === 0 ? value : value.toFixed(3);
    }
    return value;
  };

  const calculateOPS = (obp, slg) => {
    const obpVal = parseFloat(obp) || 0;
    const slgVal = parseFloat(slg) || 0;
    return (obpVal + slgVal).toFixed(3);
  };

  // Helper function to get stat value from player data
  const getStatValue = (player, column) => {
    const currentTab = filterActiveTab || (statCategory === 'PITCHING' ? 'pitching' : 'hitting');
    let stats;

    // Check if this is a fielding stat
    if (column.isFielding) {
      stats = Array.isArray(player.fieldingStats)
        ? player.fieldingStats[0]
        : player.fieldingStats;
    } else if (currentTab === 'hitting') {
      stats = Array.isArray(player.battingStats)
        ? player.battingStats[0]
        : player.battingStats;
    } else {
      stats = Array.isArray(player.pitchingStats)
        ? player.pitchingStats[0]
        : player.pitchingStats;
    }

    // Backend pitching/hitting percent + advanced stats — read directly
    const fmtPct = (v) => {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      if (isNaN(n)) return '0';
      return n > 1 ? `${n.toFixed(1)}%` : `${(n * 100).toFixed(1)}%`;
    };
    if (column.key === 'k_pct' || column.key === 'k%') {
      const v = stats?.k_percentage ?? stats?.K_percent;
      return fmtPct(v);
    }
    if (column.key === 'bb_pct' || column.key === 'bb%') {
      const v = stats?.bb_percentage ?? stats?.BB_percent;
      return fmtPct(v);
    }
    if (column.key === 'k_bb_ratio') {
      const v = stats?.k_bb_ratio ?? stats?.K_BB_ratio;
      return v === null || v === undefined ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'k_bb_pct') return fmtPct(stats?.K_BB_percent);
    if (column.key === 'lob_pct') return fmtPct(stats?.LOB_percent);
    if (column.key === 'fip') {
      const v = stats?.FIP ?? stats?.fip;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'fip_plus') {
      const v = stats?.FIP_plus ?? stats?.fip_plus;
      return v === null || v === undefined || v === '' ? '0' : Math.round(Number(v)).toString();
    }
    if (column.key === 'era_plus') {
      const v = stats?.ERA_plus ?? stats?.era_plus;
      return v === null || v === undefined || v === '' ? '0' : Math.round(Number(v)).toString();
    }
    if (column.key === 'xfip') {
      const v = stats?.xFIP ?? stats?.xfip;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(2);
    }
    if (column.key === 'babip_against') {
      const v = stats?.BABIP_against ?? stats?.babip_against;
      return v === null || v === undefined || v === '' ? '0' : Number(v).toFixed(3);
    }

    // OPS: use directly from API if available, otherwise calculate
    if (column.calculated && column.key === 'ops') {
      const directOps = stats?.on_base_plus_slugging ?? stats?.ops;
      if (directOps !== undefined && directOps !== null) {
        return formatStat(typeof directOps === 'object' && directOps.$numberDecimal ? parseFloat(directOps.$numberDecimal) : parseFloat(directOps), 'ops');
      }
      const obp = stats?.ob_pct || stats?.onBasePercentage || stats?.on_base_percentage;
      const slg = stats?.slg_pct || stats?.sluggingPercentage || stats?.slugging_percentage;
      return calculateOPS(obp, slg);
    }

    // Context-aware key mapping for pitching stats
    let actualKey = column.key;
    if (currentTab === 'pitching') {
      const pitchingKeyMapping = {
        '2b': 'doubles_allowed',
        '3b': 'triples_allowed',
        'hr': 'home_runs_allowed',
        'bb': 'walks_allowed',
      };
      actualKey = pitchingKeyMapping[column.key] || column.key;
    }

    // Define fallback mappings (same as StatsTable)
    const fallbackMapping = {
      'avg': ['avg', 'battingAverage', 'batting_average'],
      'gp': ['gp', 'games_played', 'gamesPlayed'],
      'gs': ['gs', 'games_started', 'gamesStarted'],
      'ab': ['ab', 'atBats', 'at_bats'],
      'r': ['r', 'runs'],
      'h': ['h', 'hits'],
      '2b': ['2b', 'doubles'],
      '3b': ['3b', 'triples'],
      'hr': ['hr', 'home_runs', 'homeRuns'],
      'rbi': ['rbi'],
      'tb': ['tb', 'totalBases', 'total_bases'],
      'slg_pct': ['slg_pct', 'sluggingPercentage', 'slugging_percentage'],
      'bb': ['bb', 'walks'],
      'so': ['so', 'strikeouts'],
      'ob_pct': ['ob_pct', 'onBasePercentage', 'on_base_percentage'],
      'sb': ['sb', 'stolenBases', 'stolen_bases'],
      'cs': ['cs', 'caughtStealing', 'caught_stealing'],
      'wins': ['w', 'wins'],
      'losses': ['l', 'losses'],
      'era': ['era', 'ERA'],
      'games_pitched': ['gp', 'games_pitched', 'gamesPitched', 'app', 'appearances'],
      'games_started': ['gs', 'games_started', 'gamesStarted'],
      'complete_games': ['cg', 'complete_games', 'completeGames'],
      'shutouts': ['sho', 'shutouts'],
      'saves': ['sv', 'saves'],
      'innings_pitched': ['ip', 'innings_pitched', 'inningsPitched'],
      'hits_allowed': ['h', 'hits_allowed', 'hitsAllowed', 'hits'],
      'runs_allowed': ['r', 'runs_allowed', 'runsAllowed', 'runs'],
      'earned_runs': ['er', 'earned_runs', 'earnedRuns'],
      'walks_allowed': ['bb', 'walks_allowed', 'walksAllowed', 'walks'],
      'strikeouts_pitched': ['so', 'strikeouts_pitched', 'strikeoutsPitched', 'strikeouts'],
      'home_runs_allowed': ['hr', 'home_runs_allowed', 'homeRunsAllowed', 'homeRuns'],
      'doubles_allowed': ['doubles_allowed', 'doublesAllowed', 'doubles'],
      'triples_allowed': ['triples_allowed', 'triplesAllowed', 'triples'],
      'wild_pitches': ['wp', 'wild_pitches', 'wildPitches'],
      'hit_batters': ['hbp', 'hit_batters', 'hitBatters', 'hitByPitch'],
      'balks': ['bk', 'balks'],
      'at_bats': ['ab', 'at_bats', 'atBats'],
      'batting_average_against': ['baa', 'batting_average_against', 'battingAverageAgainst', 'battingAverage'],
      'WHIP': ['whip', 'WHIP'],
      'fielding_percentage': ['fpct', 'fielding_percentage', 'fieldingPercentage'],
      'errors': ['e', 'errors'],
      'putouts': ['po', 'putouts'],
      'assists': ['a', 'assists'],
      'double_plays': ['dp', 'double_plays', 'doublePlays'],
      'total_chances': ['tc', 'total_chances', 'totalChances'],
      'stolen_bases_against': ['sba', 'stolen_bases_against', 'stolenBasesAgainst', 'stolenBasesAllowed'],
      'runners_caught_stealing': ['rcs', 'runners_caught_stealing', 'runnersCaughtStealing'],
      'runners_caught_stealing_percentage': ['rcs_pct', 'runners_caught_stealing_percentage', 'runnersCaughtPercentage'],
      'passed_balls': ['pb', 'passed_balls', 'passedBalls'],
      'ops_plus': ['ops_plus', 'OPS+', 'opsPlus', 'ops_plus_adjusted'],
      'woba': ['woba', 'wOBA', 'weighted_on_base_average'],
      'wrc_plus': ['wRC_plus', 'wrc_plus', 'wRC+', 'wrcPlus'],
      'pa': ['pa', 'plate_appearances', 'plateAppearances'],
      'babip': ['babip', 'BABIP', 'batting_average_balls_in_play'],
      'iso': ['iso', 'ISO', 'isolated_power'],
    };

    // Try to get value using fallback keys
    const possibleKeys = fallbackMapping[actualKey] || [actualKey];
    for (const key of possibleKeys) {
      if (stats?.[key] !== undefined && stats?.[key] !== null) {
        return formatStat(stats[key], actualKey);
      }
    }

    return formatStat(stats?.[actualKey], actualKey);
  };

  // Get the backend field name for a UI column
  const getBackendField = (uiColumn) => {
    if (columnToBackendSort[uiColumn]) return columnToBackendSort[uiColumn];
    if (statToBackendSort[uiColumn]) return statToBackendSort[uiColumn];
    return uiColumn;
  };

  const handleSort = (column) => {
    if (!onSort) return;
    const backendField = getBackendField(column);
    const newDirection = (sortColumn === backendField && sortDirection === 'asc') ? 'desc' : 'asc';
    onSort(backendField, newDirection);
  };

  const renderSortIcon = (column) => {
    const backendField = getBackendField(column);
    if (sortColumn !== backendField) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-500 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-3 h-3 ml-1 text-cyan-400 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1 text-cyan-400 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  if (isInitialLoading) {
    return (
      <div className="overflow-x-auto w-full scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 rounded-lg bg-slate-900/80">
        <div className="flex items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-x-auto w-full scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 rounded-lg bg-slate-900/80">
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-lg font-medium text-red-400">{error}</p>
          {onResetFilters && (
            <button
              onClick={onResetFilters}
              className="mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors text-sm"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="overflow-x-auto w-full scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900 rounded-lg bg-slate-900/80">
        <div className="text-center py-12 text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-slate-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="text-lg font-medium">No uncommitted players found</p>
          <p className="text-sm mt-2">Check back later for updates.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading Overlay - shown when refreshing data */}
      {loading && data.length > 0 && (
        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-30 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-sm font-medium">Loading players...</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto w-full max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
        <table className="min-w-full text-xs border-collapse">
          <thead className="bg-slate-900/60 sticky top-0 z-10">
            <tr className="text-white">
              {/* <th
                onClick={() => handleSort('jpRank')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                JP RANK{renderSortIcon('jpRank')}
              </th> */}
              <th
                onClick={() => handleSort('year')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                YEAR{renderSortIcon('year')}
              </th>
              <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap">
                <div className="flex items-center gap-0.5 justify-center">
                  <HeaderSearchBox
                    label="NAME"
                    value={nameSearch}
                    onSearch={onNameSearch}
                    placeholder="Search player..."
                  />
                  <span
                    onClick={() => handleSort('name')}
                    className="cursor-pointer hover:text-cyan-400 transition-colors"
                  >
                    {renderSortIcon('name')}
                  </span>
                </div>
              </th>

              <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap">
                <MultiSelectDropdown
                  selected={selectedPositions}
                  onChange={onPositionFilter}
                  label="POSITION"
                />
              </th>
              <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap">
                <HeaderTeamDropdown
                  label="TEAM"
                  selectedTeam={selectedTeam}
                  onSelect={onTeamSelect}
                />
              </th>
              {showNewSchool && (
                <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap">
                  NEW SCHOOL
                </th>
              )}
              <th
                onClick={() => handleSort('class')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                CLASS{renderSortIcon('class')}
              </th>
              <th
                onClick={() => handleSort('region')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                HOMETOWN{renderSortIcon('region')}
              </th>
              {/* <th
                onClick={() => handleSort('playerScore')}
                className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap cursor-pointer hover:bg-slate-800/60 transition-colors"
              >
                PLAYER SCORE{renderSortIcon('playerScore')}
              </th> */}
              {/* Dynamic stat columns */}
              {dynamicStatColumns.map((column, index) => (
                <th
                  key={`stat-${index}`}
                  onClick={() => handleSort(column.statId || column.key)}
                  className="px-2 sm:px-3 py-2 sm:py-3 font-semibold text-center text-[10px] sm:text-xs whitespace-nowrap bg-slate-900/60 cursor-pointer hover:bg-slate-800/60 transition-colors"
                >
                  {column.label}{renderSortIcon(column.statId || column.key)}
                </th>
              ))}
              <th className="px-2 sm:px-3 py-2 sm:py-3 font-semibold sticky right-0 bg-slate-900/95 z-20 text-center text-[10px] sm:text-xs">
                {/* ACTIONS */}
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((player, idx) => (
              <PlayerRow
                player={player}
                key={player._id || idx}
                followedPlayers={followedPlayers}
                showNewSchool={showNewSchool}
                dynamicStatColumns={dynamicStatColumns}
                getStatValue={getStatValue}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination && onPageChange && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-slate-700/50">
          {/* Page Info + Limit Selector */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              Showing {data.length} players on page {pagination.currentPage || 1} of {pagination.totalPages || 1}
              <span className="ml-2">({pagination.totalCount || 0} total players)</span>
            </span>
            {onLimitChange && (
              <SimpleDropdown
                value={limit}
                onChange={(val) => onLimitChange(Number(val))}
                options={[
                  { label: '10 / page', value: 10 },
                  { label: '20 / page', value: 20 },
                  { label: '50 / page', value: 50 },
                  { label: '100 / page', value: 100 },
                ]}
                variant="admin"
                dropUp
              />
            )}
          </div>

          {/* Pagination Buttons */}
          <div className="flex items-center gap-2">
            {/* Previous Button */}
            <button
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1 || loading}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {/* First page */}
              {pagination.currentPage > 3 && (
                <>
                  <button
                    onClick={() => onPageChange(1)}
                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                  >
                    1
                  </button>
                  {pagination.currentPage > 4 && (
                    <span className="text-gray-400 px-2">...</span>
                  )}
                </>
              )}

              {/* Current page and neighbors */}
              {Array.from({ length: 5 }, (_, i) => pagination.currentPage - 2 + i)
                .filter(page => page > 0 && page <= pagination.totalPages)
                .map(page => (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    disabled={page === pagination.currentPage || loading}
                    className={`px-3 py-2 font-medium rounded-lg transition-colors duration-200 text-sm ${page === pagination.currentPage
                      ? 'bg-cyan-600 text-white cursor-default'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                      }`}
                  >
                    {page}
                  </button>
                ))}

              {/* Last page */}
              {pagination.currentPage < pagination.totalPages - 2 && (
                <>
                  {pagination.currentPage < pagination.totalPages - 3 && (
                    <span className="text-gray-400 px-2">...</span>
                  )}
                  <button
                    onClick={() => onPageChange(pagination.totalPages)}
                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                  >
                    {pagination.totalPages}
                  </button>
                </>
              )}
            </div>

            {/* Next Button */}
            <button
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages || loading}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
